import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

// ── Time Adjust Modal ─────────────────────────────────────────────────────────
function TimeAdjustModal({ attempt, onClose, onConfirm }) {
  const [minutes, setMinutes] = useState(5);
  const [action,  setAction]  = useState('add');
  const [reason,  setReason]  = useState('');
  const [loading, setLoading] = useState(false);
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <h3 className="modal-title">⏱ Adjust Time — {attempt.student_name}</h3>
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {['add','deduct'].map(a => (
            <button key={a} className="btn btn-secondary btn-sm" onClick={() => setAction(a)}
              style={action===a?{borderColor:'var(--accent-primary)',color:'var(--accent-primary)',background:'rgba(79,142,247,0.1)'}:{}}>
              {a==='add' ? '+ Add Time' : '− Deduct Time'}
            </button>
          ))}
        </div>
        <div className="form-group">
          <label className="form-label">Minutes</label>
          <input className="form-input" type="number" min="1" max="120" value={minutes}
            onChange={e => setMinutes(parseInt(e.target.value)||1)} style={{ width:100 }} />
        </div>
        <div className="form-group">
          <label className="form-label">Reason (optional)</label>
          <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Technical issue, grace period..." />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className={`btn ${action==='add'?'btn-success':'btn-danger'}`} disabled={loading}
            onClick={async () => {
              setLoading(true);
              try { await onConfirm(attempt.id, action==='add'?minutes:-minutes, reason); onClose(); }
              finally { setLoading(false); }
            }}>
            {loading ? 'Applying...' : action==='add' ? `+ Add ${minutes} min` : `− Deduct ${minutes} min`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resume Modal ──────────────────────────────────────────────────────────────
function ResumeModal({ attempt, onClose, onConfirm }) {
  const [extra,       setExtra]       = useState(0);
  const [useFullTime, setUseFullTime] = useState(false); // proctor choice
  const [loading,     setLoading]     = useState(false);
  const remainingMins = attempt.remaining_seconds ? Math.ceil(attempt.remaining_seconds/60) : 0;
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <h3 className="modal-title">▶ Resume Exam</h3>
        <div style={{ fontSize:14, color:'var(--text-secondary)', marginBottom:14 }}>
          <div><strong>Student:</strong> {attempt.student_name}</div>
          <div><strong>Exam:</strong> {attempt.exam_title}</div>
          <div style={{ marginTop:4 }}><strong>Time when terminated:</strong>{' '}
            <span style={{ fontFamily:'var(--font-mono)', color:'var(--accent-warning)' }}>
              {remainingMins} min remaining
            </span>
          </div>
        </div>

        {/* Time mode choice */}
        <div style={{ marginBottom:16 }}>
          <label className="form-label">Resume with</label>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <button onClick={() => setUseFullTime(false)}
              style={{ flex:1, padding:'10px 14px', borderRadius:8, border:'2px solid', cursor:'pointer', textAlign:'center', fontSize:13,
                borderColor: !useFullTime ? 'var(--accent-primary)' : 'var(--border)',
                background:  !useFullTime ? 'rgba(79,142,247,0.1)' : 'transparent',
                color:       !useFullTime ? 'var(--accent-primary)' : 'var(--text-secondary)', fontWeight: !useFullTime ? 700 : 400 }}>
              <div style={{ fontSize:20, marginBottom:4 }}>⏱</div>
              <div>Remaining Time</div>
              <div style={{ fontSize:11, marginTop:2, fontFamily:'var(--font-mono)' }}>{remainingMins} min</div>
            </button>
            <button onClick={() => setUseFullTime(true)}
              style={{ flex:1, padding:'10px 14px', borderRadius:8, border:'2px solid', cursor:'pointer', textAlign:'center', fontSize:13,
                borderColor: useFullTime ? 'var(--accent-success)' : 'var(--border)',
                background:  useFullTime ? 'rgba(16,185,129,0.1)' : 'transparent',
                color:       useFullTime ? 'var(--accent-success)' : 'var(--text-secondary)', fontWeight: useFullTime ? 700 : 400 }}>
              <div style={{ fontSize:20, marginBottom:4 }}>⏰</div>
              <div>Full Duration</div>
              <div style={{ fontSize:11, marginTop:2, fontFamily:'var(--font-mono)' }}>Fresh start</div>
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">+ Extra minutes (optional bonus)</label>
          <input className="form-input" type="number" min="0" max="120" value={extra}
            onChange={e => setExtra(parseInt(e.target.value)||0)} style={{ width:100 }} />
        </div>
        <div className="alert alert-info" style={{ marginBottom:0, marginTop:8, fontSize:12 }}>
          Student's previous answers will be restored automatically.
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" disabled={loading}
            onClick={async () => {
              setLoading(true);
              try { await onConfirm(attempt.id, extra, useFullTime); onClose(); }
              finally { setLoading(false); }
            }}>
            {loading ? 'Resuming...' : `▶ Resume (${useFullTime ? 'Full time' : `${remainingMins}min`}${extra ? ` +${extra}` : ''})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── InstructorDashboard ──────────────────────────────────────────────────────
export default function InstructorDashboard() {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const [exams,         setExams]         = useState([]);
  const [attempts,      setAttempts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState('all');
  const [resumeAttempt, setResumeAttempt] = useState(null);
  const [adjustAttempt, setAdjustAttempt] = useState(null);

  const fetchData = useCallback(() => {
    Promise.all([
      axios.get('/api/exams/manage'),
      axios.get('/api/attempts/all'),
    ]).then(([exRes, atRes]) => {
      setExams(exRes.data);
      setAttempts(atRes.data);
    }).catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResume = async (attemptId, extra, useFullTime=false) => {
    const res = await axios.post(`/api/attempts/${attemptId}/resume`, { extra_minutes: extra, use_full_time: useFullTime });
    toast.success(`Resumed — ${Math.ceil(res.data.remaining_seconds/60)} min left`);
    fetchData();
  };

  const handleAdjustTime = async (attemptId, minutes, reason) => {
    await axios.post(`/api/attempts/${attemptId}/adjust-time`, { minutes, reason });
    toast.success(`Time ${minutes>0?'added':'deducted'}: ${Math.abs(minutes)} min`);
    fetchData();
  };

  const handleTerminate = async (a) => {
    const reason = window.prompt(`Terminate exam for ${a.student_name}?\nReason:`, 'Proctor terminated');
    if (reason === null) return;
    try {
      await axios.post(`/api/attempts/${a.id}/terminate`, { reason });
      toast.success('Exam terminated');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const filteredAttempts = filter === 'all'         ? attempts
    : filter === 'in_progress' ? attempts.filter(a => a.status === 'in_progress')
    : filter === 'terminated'  ? attempts.filter(a => a.status === 'terminated')
    : attempts.filter(a => (a.trust_score||100) < 50);

  const activeAttempts = attempts.filter(a => a.status === 'in_progress').length;
  const terminatedCount = attempts.filter(a => a.status === 'terminated').length;
  const totalSubmitted  = attempts.filter(a => a.status === 'submitted').length;

  const statusBadge = s => {
    const m = { in_progress:'badge-blue', submitted:'badge-green', terminated:'badge-red', graded:'badge-green' };
    return <span className={`badge ${m[s]||'badge-yellow'}`}>{s?.replace('_',' ')}</span>;
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">

        {/* Header */}
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h1 className="page-title">Instructor Dashboard</h1>
            <p className="page-subtitle">
              {user?.full_name} · {user?.org_name || 'No organization'}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/manage/exams')}>
            ◉ My Exams
          </button>
        </div>

        {/* Stats */}
        <div className="card-grid card-grid-4 mb-6">
          {[
            { val: exams.length,     label:'My Exams',    color:'var(--accent-primary)'   },
            { val: activeAttempts,   label:'In Progress', color:'var(--accent-warning)'   },
            { val: totalSubmitted,   label:'Submitted',   color:'var(--accent-success)'   },
            { val: terminatedCount,  label:'Terminated',  color:'var(--accent-danger)'    },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-value" style={{ color:s.color }}>{s.val}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* My Exams quick panel */}
        {exams.length > 0 && (
          <div className="card mb-4">
            <h3 style={{ fontSize:15, fontWeight:600, marginBottom:14 }}>My Exams</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
              {exams.map(ex => (
                <div key={ex.id} style={{
                  padding:'12px 16px', background:'var(--bg-elevated)',
                  borderRadius:'var(--radius-sm)', border:'1px solid var(--border)',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ fontWeight:600, fontSize:13, flex:1, marginRight:8 }}>{ex.title}</div>
                    <span className={`badge badge-${ex.is_published?'green':'yellow'}`}>
                      {ex.is_published ? 'Live' : 'Draft'}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10 }}>
                    {ex.question_count} questions · {ex.duration_minutes}min · {ex.enrolled_count||0} enrolled
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-secondary btn-sm" style={{ flex:1, justifyContent:'center' }}
                      onClick={() => navigate(`/live/${ex.id}`)}>📡 Live Monitor</button>
                    <button className="btn btn-secondary btn-sm" style={{ flex:1, justifyContent:'center' }}
                      onClick={() => navigate('/manage/exams')}>✏️ Edit</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attempts table */}
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ fontSize:15, fontWeight:600, margin:0 }}>
              Student Attempts ({attempts.length})
            </h3>
            <div style={{ display:'flex', gap:6 }}>
              {[['all','All'],['in_progress','Active'],['terminated','Terminated'],['low_trust','Low Trust']].map(([val,label]) => (
                <button key={val} className="btn btn-secondary btn-sm" onClick={() => setFilter(val)}
                  style={filter===val?{borderColor:'var(--accent-primary)',color:'var(--accent-primary)',background:'rgba(79,142,247,0.1)'}:{}}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="table-container" style={{ border:'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Exam</th>
                  <th>Status</th>
                  <th>Trust Score</th>
                  <th>Time Left</th>
                  <th>IP</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttempts.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign:'center', padding:32, color:'var(--text-secondary)' }}>
                      No attempts found
                    </td>
                  </tr>
                )}
                {filteredAttempts.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight:500 }}>{a.student_name}</td>
                    <td style={{ fontSize:13, color:'var(--text-secondary)', maxWidth:200 }}>
                      <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.exam_title}</div>
                    </td>
                    <td>{statusBadge(a.status)}</td>
                    <td>
                      <span style={{
                        fontFamily:'var(--font-mono)', fontSize:13, fontWeight:600,
                        color:(a.trust_score||100)>=75?'var(--accent-success)':(a.trust_score||100)>=40?'var(--accent-warning)':'var(--accent-danger)'
                      }}>
                        {Math.round(a.trust_score||100)}%
                      </span>
                    </td>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                      {a.remaining_seconds ? `${Math.ceil(a.remaining_seconds/60)}m` : '—'}
                    </td>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-secondary)' }}>
                      {a.ip_address || '—'}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:5 }}>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => navigate(`/proctor/${a.id}`)}>Report</button>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => navigate(`/live/${a.exam_id}`)}>Live</button>
                        {a.status === 'terminated' && (
                          <button className="btn btn-success btn-sm"
                            onClick={() => setResumeAttempt(a)}>▶</button>
                        )}
                        {a.status === 'in_progress' && (<>
                          <button className="btn btn-secondary btn-sm"
                            style={{ color:'var(--accent-warning)' }}
                            onClick={() => setAdjustAttempt(a)}>⏱</button>
                          <button className="btn btn-danger btn-sm"
                            onClick={() => handleTerminate(a)}>⛔</button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {resumeAttempt && (
        <ResumeModal attempt={resumeAttempt} onClose={() => setResumeAttempt(null)} onConfirm={handleResume} />
      )}
      {adjustAttempt && (
        <TimeAdjustModal attempt={adjustAttempt} onClose={() => setAdjustAttempt(null)} onConfirm={handleAdjustTime} />
      )}
    </div>
  );
}
